import { useMemo } from 'react';
import { ui } from '../ui';
import { colors } from '../theme';

const DAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const SLOT_MINUTES = 20;
const DEFAULT_START_HOUR = 9;
const DEFAULT_END_HOUR = 21;

export const STATUS_STYLE = {
  open: { bg: 'rgba(111,191,139,0.16)', fg: colors.good, label: 'Свободно' },
  booked: { bg: 'rgba(224,171,85,0.18)', fg: colors.warn, label: 'Забронировано' },
  completed: { bg: colors.surface2, fg: colors.textMuted, label: 'Проведено' },
  no_show: { bg: 'rgba(217,128,128,0.16)', fg: colors.danger, label: 'Неявка' },
  cancelled: { bg: colors.surface2, fg: colors.textMuted, label: 'Отменён' },
};

function addMinutes(time, minutes) {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
}

function pad(n) { return String(n).padStart(2, '0'); }
export function dateKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function timeKey(d) { return `${pad(d.getHours())}:${pad(d.getMinutes())}`; }

export function getMonday(weekOffset) {
  const now = new Date();
  const day = now.getDay() || 7;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day + 1);
  monday.setDate(monday.getDate() + weekOffset * 7);
  return monday;
}

function fmtDay(d) {
  return d.toLocaleDateString('ru', { day: '2-digit', month: '2-digit' });
}

/**
 * Недельная календарная сетка слотов интервью. Переиспользуется и в личном
 * календаре куратора (интерактивно), и в обзорном календаре супер-админа (только просмотр).
 */
export default function CalendarGrid({ slots, onSlotClick, weekOffset, onWeekChange }) {
  const weekDays = useMemo(() => {
    const monday = getMonday(weekOffset);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekOffset]);

  const weekDayKeys = useMemo(() => new Set(weekDays.map(dateKey)), [weekDays]);

  const slotsInWeek = useMemo(
    () => slots.filter(s => weekDayKeys.has(dateKey(new Date(s.start_at)))),
    [slots, weekDayKeys]
  );

  const { startHour, endHour } = useMemo(() => {
    let start = DEFAULT_START_HOUR;
    let end = DEFAULT_END_HOUR;
    slotsInWeek.forEach(s => {
      const h = new Date(s.start_at).getHours();
      if (h < start) start = h;
      if (h + 1 > end) end = h + 1;
    });
    return { startHour: start, endHour: end };
  }, [slotsInWeek]);

  const rows = useMemo(() => {
    // Стандартная сетка на :00/:20/:40 — плюс реальные времена слотов, если они почему-то
    // не выровнены (например, старые данные) — чтобы такой слот не потерялся молча.
    const set = new Set();
    for (let h = startHour; h < endHour; h++) {
      for (let m = 0; m < 60; m += SLOT_MINUTES) {
        set.add(`${pad(h)}:${pad(m)}`);
      }
    }
    slotsInWeek.forEach(s => set.add(timeKey(new Date(s.start_at))));
    return [...set].sort();
  }, [startHour, endHour, slotsInWeek]);

  const slotGrid = useMemo(() => {
    const map = {};
    slotsInWeek.forEach(s => {
      const d = new Date(s.start_at);
      map[`${dateKey(d)}_${timeKey(d)}`] = s;
    });
    return map;
  }, [slotsInWeek]);

  return (
    <div>
      <div style={{ ...ui.row, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={ui.btnSecondary} onClick={() => onWeekChange(weekOffset - 1)}>← Пред. неделя</button>
          <button style={ui.btnSecondary} onClick={() => onWeekChange(0)}>Сегодня</button>
          <button style={ui.btnSecondary} onClick={() => onWeekChange(weekOffset + 1)}>След. неделя →</button>
        </div>
        <div style={{ color: colors.textMuted, fontSize: 13 }}>
          {fmtDay(weekDays[0])} – {fmtDay(weekDays[6])}
        </div>
      </div>

      <div style={{ ...ui.card, overflow: 'auto', maxHeight: '70vh' }}>
        <table style={{ ...ui.table, tableLayout: 'fixed', minWidth: 760 }}>
          <thead>
            <tr>
              <th style={{ ...ui.th, width: 60, position: 'sticky', top: 0, background: colors.surface, zIndex: 2 }}></th>
              {weekDays.map(d => (
                <th key={dateKey(d)} style={{ ...ui.th, position: 'sticky', top: 0, background: colors.surface, zIndex: 2 }}>
                  {DAY_LABELS[d.getDay() === 0 ? 6 : d.getDay() - 1]} {fmtDay(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(time => (
              <tr key={time}>
                <td style={{ ...ui.td, color: colors.textMuted, fontSize: 12, whiteSpace: 'nowrap' }}>
                  {time}–{addMinutes(time, SLOT_MINUTES)}
                </td>
                {weekDays.map(d => {
                  const key = `${dateKey(d)}_${time}`;
                  const slot = slotGrid[key];
                  const style = slot ? STATUS_STYLE[slot.status] : null;
                  return (
                    <td
                      key={key}
                      onClick={() => slot && onSlotClick && onSlotClick(slot)}
                      style={{
                        ...ui.td, padding: 4, cursor: slot && onSlotClick ? 'pointer' : 'default',
                        background: style ? style.bg : 'transparent',
                      }}
                    >
                      {slot && (
                        <div style={{ fontSize: 11, color: style.fg, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>{slot.status === 'booked' ? (slot.user_name || 'Занято') : style.label}</span>
                          {slot.status === 'booked' && slot.meeting_link && (
                            <span title="Ссылка на собеседование добавлена" style={{ color: colors.good }}>✓</span>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
